// src/utils/calendar.ts
//
// „Přidat do kalendáře" (nice-to-have po redesignu, rozhodnuto 2. 10. 2026):
// závod jako celodenní událost – soubor .ics (iCalendar, RFC 5545; Apple
// Kalendář, Outlook, Android) a odkaz do Google Kalendáře. Závod má jen
// datum bez času (races.date "YYYY-MM-DD"), proto celodenní událost
// s připomínkou den předem v 9:00.

import { stripDiacritics } from "../sepa";
import { disciplineOf } from "./disciplines";

export interface CalendarEvent {
  /** Stabilní identifikátor – opakované přidání stejného závodu událost aktualizuje. */
  uid: string;
  title: string;
  /** Den konání "YYYY-MM-DD" (celodenní událost). */
  date: string;
  location: string;
  description: string;
  /** Odkaz na závod v aplikaci; "" = bez odkazu. */
  url: string;
}

/** Den "YYYY-MM-DD" → "YYYYMMDD"; neplatné datum → "". */
function compactDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!m) return "";
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (
    d.getUTCFullYear() !== Number(m[1]) ||
    d.getUTCMonth() !== Number(m[2]) - 1 ||
    d.getUTCDate() !== Number(m[3])
  ) {
    return "";
  }
  return `${m[1]}${m[2]}${m[3]}`;
}

/** Následující den "YYYYMMDD" (konec celodenní události je výlučný). */
function nextDay(compact: string): string {
  const d = new Date(
    Date.UTC(Number(compact.slice(0, 4)), Number(compact.slice(4, 6)) - 1, Number(compact.slice(6, 8)) + 1)
  );
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;
}

/** Okamžik v UTC pro DTSTAMP: "YYYYMMDDTHHMMSSZ". */
function utcStamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

/** Text hodnoty TEXT podle RFC 5545 (zpětné lomítko, středník, čárka, nový řádek). */
export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}

/** Počet bajtů znaku v UTF-8 (podle kódu znaku). */
function utf8Length(char: string): number {
  const code = char.codePointAt(0) || 0;
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  if (code < 0x10000) return 3;
  return 4;
}

/**
 * Zalomení řádku na nejvýš 75 bajtů (RFC 5545 3.1): pokračování začíná
 * mezerou. Zalamuje se mezi znaky, ne uprostřed vícebajtového znaku.
 */
export function foldIcsLine(line: string): string {
  const parts: string[] = [];
  let current = "";
  let bytes = 0;
  // for…of prochází celé znaky (i dvojice náhradních kódů)
  for (const char of line) {
    const size = utf8Length(char);
    const limit = parts.length === 0 ? 75 : 74; // pokračování má navíc mezeru
    if (bytes + size > limit) {
      parts.push(current);
      current = "";
      bytes = 0;
    }
    current += char;
    bytes += size;
  }
  parts.push(current);
  return parts.join("\r\n ");
}

/** Obsah souboru .ics s jednou celodenní událostí; neplatné datum → "". */
export function buildIcs(event: CalendarEvent, now: Date): string {
  const start = compactDate(event.date);
  if (!start) return "";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//LKS//LKS Apka//CS",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${event.uid}`,
    `DTSTAMP:${utcStamp(now)}`,
    `DTSTART;VALUE=DATE:${start}`,
    `DTEND;VALUE=DATE:${nextDay(start)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
  ];
  if (event.location) lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  if (event.description) lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  if (event.url) lines.push(`URL:${event.url}`);
  lines.push(
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeIcsText(`Zítra: ${event.title}`)}`,
    // Celodenní událost začíná o půlnoci → 15 h předem = den předem v 9:00
    "TRIGGER:-PT15H",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR"
  );
  return lines.map(foldIcsLine).join("\r\n") + "\r\n";
}

/** Odkaz do Google Kalendáře (formulář nové události); neplatné datum → "". */
export function googleCalendarUrl(event: CalendarEvent): string {
  const start = compactDate(event.date);
  if (!start) return "";
  const details = event.url ? `${event.description}\n\n${event.url}`.trim() : event.description;
  const params = [
    "action=TEMPLATE",
    `text=${encodeURIComponent(event.title)}`,
    `dates=${start}/${nextDay(start)}`,
    `details=${encodeURIComponent(details)}`,
    `location=${encodeURIComponent(event.location)}`,
  ];
  return `https://calendar.google.com/calendar/render?${params.join("&")}`;
}

/** Odkaz na mapu (URL) se do místa události nehodí – patří do popisu. */
const isUrl = (value: string): boolean => /^https?:\/\//i.test(value);

/** Závod z races/{id} – jen pole, která událost potřebuje. */
export interface CalendarRace {
  id: string;
  name?: string;
  /** "YYYY-MM-DD". */
  date?: string;
  discipline?: string | null;
  shootingRange?: string;
  /** Adresa / položka našeptávače, nebo odkaz na mapu. */
  mapUrl?: string;
  /** false = závod jiného pořadatele; chybí = LKS (starší závody pole nemají). */
  organizedByLKS?: boolean;
}

/**
 * Událost závodu: název, celodenní den konání, místo = střelnice (+ adresa,
 * když je mapUrl text), popis = disciplína (+ odkaz na mapu) a odkaz na
 * závod v aplikaci (`origin` = adresa aplikace, např. window.location.origin).
 * Závod jiného pořadatele (organizedByLKS === false) se za závod LKS nevydává.
 */
export function raceCalendarEvent(race: CalendarRace, origin: string): CalendarEvent {
  const byLks = race.organizedByLKS !== false;
  const name = (race.name || "").trim() || (byLks ? "Závod LKS" : "Závod");
  const range = (race.shootingRange || "").trim();
  const place = (race.mapUrl || "").trim();
  const location = [range, place && !isUrl(place) && place !== range ? place : ""]
    .filter(Boolean)
    .join(", ");
  const descriptionLines = [
    byLks
      ? `Závod LKS – ${disciplineOf(race).label}`
      : `Závod – ${disciplineOf(race).label} (nepořádá LKS)`,
  ];
  if (place && isUrl(place)) descriptionLines.push(`Mapa: ${place}`);
  const base = origin.replace(/\/+$/, "");
  return {
    uid: `race-${race.id}@lks-apka`,
    title: name,
    date: (race.date || "").slice(0, 10),
    location,
    description: descriptionLines.join("\n"),
    url: base ? `${base}/payment/${encodeURIComponent(race.id)}` : "",
  };
}

/** Jméno souboru: "lks-2026-09-26-mcr-malorazka.ics" (bez diakritiky a mezer). */
export function icsFileName(event: CalendarEvent): string {
  const slug = stripDiacritics(event.title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
  return `lks-${event.date}${slug ? `-${slug}` : ""}.ics`;
}
